export default {
  id: 'risk-profile-setup',
  title: 'Risk Profile Setup',
  content: `## Risk Profile Setup

Your Risk Profile determines how the platform understands your financial situation, trading knowledge, and behavioral patterns. It produces a score from 0 to 100 that drives your recommended risk per trade, trading style, and daily trade limit.

> Complete all 13 questions before placing your first trade. Partial answers produce an incomplete score.

---

## Where to Find It

**Profile → Risk Profile**

The questionnaire uses a card-by-card flow — one question per screen. Your answers are saved automatically after each selection. You can edit any answer later without retaking the full quiz.

---

## How the Score Is Calculated

Your score is built from three weighted categories:

| Category | Weight | Questions |
|---|---|---|
| Financial Capacity | 30% | Age, income, allocation %, loan burden, dependents |
| Knowledge & Experience | 20% | Trading experience, TA knowledge |
| Behavioral Traits | 50% | Capital source, loss tolerance, holding behavior, SL reaction, trading goal, response to consecutive losses |

Behavior carries the most weight because it is the strongest predictor of trading outcomes.

---

## Score Bands

| Score | Profile | Risk / Trade | Max Trades / Day |
|---|---|---|---|
| 0 – 25 | Very Conservative | 0.5% | 1 |
| 26 – 45 | Conservative | 1.0% | 2 |
| 46 – 65 | Moderate | 1.5% | 3 |
| 66 – 80 | Aggressive | 2.0% | 5 |
| 81 – 100 | Very Aggressive | 2.5% | 8 |

---

## The 13 Questions

### Financial Capacity (5 questions)

**1. Age group**
Younger traders have more time to recover from losses. Under 25 scores higher than over 50.

**2. Monthly income**
Higher income = more financial cushion. Above ₹1L scores highest.

**3. Income allocation to trading**
10–20% scores highest — it reflects disciplined, sustainable allocation. Allocating more than 40% is penalised as over-exposure.

**4. Loan burden**
No active loans scores highest. EMI above 50% of income scores zero — you should not be taking trading risk on borrowed money.

**5. Number of dependents**
No dependents = full flexibility. Three or more dependents = lower capacity score.

---

### Knowledge & Experience (2 questions)

**6. Trading experience**
3+ years scores highest. Never traded scores lowest but is not penalised severely — knowledge can be built.

**7. Technical analysis knowledge**
Expert (charts, indicators, patterns) scores highest. No knowledge scores zero.

---

### Behavioral Traits (6 questions)

**8. Capital source**
Pure surplus savings scores highest. Borrowed capital scores zero — trading with money you cannot afford to lose is the single biggest behavioral risk.

**9. Max loss tolerance per trade**
2–5% scores highest — it reflects a realistic, professional loss limit. Under 1% and over 5% both score lower.

**10. How long you hold a losing trade**
Exiting same day scores highest. Holding until recovery scores zero — this is the averaging-down trap.

**11. Reaction when SL is hit**
Exiting immediately scores highest. Averaging down scores zero.

**12. Primary trading goal**
Long-term wealth scores highest. Income and learning score equally lower — not because they are wrong goals, but because they can encourage overtrading.

**13. Action after 3 consecutive losses**
Stopping to reflect scores highest. Doubling down to recover scores zero.

---

## What the Score Drives

| Output | How it is used |
|---|---|
| Risk % per trade | Pre-filled in Position Size Calculator and shown in OrderModal |
| Max trades per day | Shown as recommendation in Position Size Calculator |
| Trading style | Displayed as a system recommendation (does not affect your score) |
| Discipline score | Risk adherence component checks if your actual trades respect the recommended risk % |

---

## Editing Your Answers

Tap **Edit Answers** on the score screen to review and change any individual answer. Changes take effect immediately and your score updates automatically.

---

## Recommendations vs Enforcement

Risk % and max trades are **recommendations**, not hard limits. You can trade beyond them. The platform will show warnings in OrderModal and flag violations in your Discipline Score under the Risk Adherence component.
`,
};
